/* Copyright (c) 2021 MOCA Systems, Inc.  All rights reserved. */

//Current problem, mapping between ticket.edit and TicketEditService
// The following flow should be viable, excluding the arrows
//
// EditTickets(tickets)
// - builds the central edit model that the ui gets directly bound to from the database state (maybe the live state? could get interesting)
// - subscribe to observer stream to react to edit changes
// - change view on each ticket to edit, via observer chain, make a ticket_edited event maybe?
//
//
// sample 1: editing description in tecketEdit model:
// - bind to blur or changed+soak, or changed+observer based soak
// - call TicketActions.updateEditTicket(ticket)
// -- it nexts an observer
// eeeeeeee two way binding problem <----------------------------------------------
// -- update observer gets called with the same data...
//
//
// sample 2: editing width while dragging edge
// - it nexts many observers during the drag
// - drop edge
// - call TicketActions.updateTicket(ticket)
// -- it nexts an observer
// -- ticketEdit handles observer by recalcing it's data? <-------------------------
// --- scary
// -- we may want to throttle the observer in ticketEdit
//
//
//
//
// changes to ticket.view need to propaogate to ticketEdit so that width changes make it through
// changes to ticketEdit need to propaogate to ticket.view for the liveness
// changes to ticket.database should not propagate to either, this is handled by ticket.view

import { AngularFireAction, DatabaseSnapshot } from '@angular/fire/compat/database';

import { Observable, BehaviorSubject, from, of, merge, EMPTY } from 'rxjs';
import {
	scan,
	filter,
	pluck,
	first,
	bufferTime,
	skipWhile,
	mergeMap,
	switchMap,
	debounce,
	groupBy,
	takeUntil,
	map,
	withLatestFrom,
} from 'rxjs/operators';
import { drop, runInZone } from 'src/app/plain-javascript/services/rxjs-internal-extensions';

import { AngularLandConnection, FancyJoinRequirements } from '../../fancy-firebase/base/interfaces';
import { FancyFirebaseList } from '../../fancy-firebase/base/FancyFirebaseList';
import { FancyAction } from '../../fancy-firebase/base/FancyAction';
import { FancyActionTypes } from '../../fancy-firebase/base/FancyActionTypes';
import { monitorSubList } from '../../fancy-firebase/base/monitorSubList';
import { IPredecessorData, defaultDependencyData } from 'src/app/app-data/models/Dependencies';

import {
	createPromisePin,
	cloneStack,
	Ticket,
	TicketRenderTypes,
	Side,
	defaultActiveTicketDuplicateOffset,
	defaultPullTicketDuplicateOffset,
	Plan,
	Variance,
	TicketScale,
} from '@touchplan/common';

import { FloatingTicket } from 'src/app/app-data/models/FloatingTicket';
import { CalendarWorkdays, decorateTicketExtras, getTicketExtraCallbacksForPlan } from '@touchplan/ticket';
import { ticketCopy } from 'src/app/plain-javascript/services/TicketCopy';

import {
	FancyTicketAction,
	TicketActionTypes,
	CombinedTicketActions,
	TicketDuplicatedActionInput,
	ticketAddActions,
	ticketRemoveActions,
	thisClientTicketAddedActions,
} from 'src/app/app-data/actions/ticket-actions';

import { SupplementalActionTypes, SessionSettings } from 'src/app/app-data/actions/supplemental-actions';

import { TicketSelection } from 'src/app/app-data/models/TicketSelection';

import { renderQueue } from 'src/app/plain-javascript/services/RenderQueue';

import { SoakPersistence } from 'src/app/plain-javascript/services/SoakPersistence';
import { IHasType, isActionIncluded } from '../../fancy-firebase/util/is-action-included';
import { PlanAccess } from 'src/app/app-data/models/PlanAccess';

export interface IEditTicketsState {
	lastActionType: FancyAction;
	list: Map<string, Ticket>;
	changed: boolean;
}

interface ITicketAngularLandConnection extends AngularLandConnection {
	ngZone: Zone;
}

// technically duplicates the Ticket PrevData interface typing
// but keyof stuff escaping the class wreaks havoc on poorly typed code
type supportedJoinKeys =
	| 'roleId'
	| 'locationId'
	| 'requestedByProjectMemberId'
	| 'requestedByProjectMemberRoleId'
	| 'responsibleProjectMemberId'
	| 'responsibleProjectMemberRoleId'
	| 'assignedProjectMemberId'
	| 'assignedProjectMemberRoleId'
	| 'priorityId'
	| 'ticketExceptionDays'
	| '$id';

// Keep TicketList very lean, if it requires access to something in the injector, that method does not belong on the TicketList.
// - it will additionally not have access to the state outside of it's own internal state
// - this is potentially limiting, and potentially breaks up the api into peices
// - logically we'll want a TicketActions injectable which is where the api will go for manipulating Tickets.
// This should have access to the state and injectables.
// - the idea of TicketActions would be to loosely replicate the idea of reducer actions
//   without the limitations of immutability.
// - instead of a ticket action referencing a central store, the action methods will be provided the instance of tickets to operate on.
// - alternatively it could manipulate planState instead of passing in the instance of tickets
// we _will_ infact need some of the factory implementation to get at the angularfire instance when initializing.

/**
 * .editTicket$ will be notified of updates if this returns true
 */
function isEditInterested(action: FancyTicketAction) {
	return (
		action.type === TicketActionTypes.ticketChangedToEdit ||
		action.type === TicketActionTypes.ticketChangedToDefault ||
		action.payload.renderType === TicketRenderTypes.edit
	);
}

export class TicketList
	extends FancyFirebaseList<Ticket, FancyTicketAction>
	implements FancyJoinRequirements<Ticket>
{
	//derived properties
	public get editTicketCountSync() {
		return this.editTicket$.getValue().size;
	}
	public get selectedTicketCountSync() {
		return this.selectedTicket$.getValue().list.size;
	}

	constructor(
		protected angularLand: ITicketAngularLandConnection,
		protected path: any,
		fancyJoins: any,
		protected workDays: CalendarWorkdays
	) {
		super(angularLand, path, FancyTicketAction, fancyJoins);
		this.suppressActionsForLoading = true;
		this.enableLogging = true;
		this.logger.setContext('TicketList');

		this.letEditTicketsPlay();
		this.setupSelection();
		this.makePullList();
		this.setupBufferedEvents();

		this.soakPersistence.output$
			.pipe(takeUntil(this.shutdownSubject))
			.subscribe((obj) => this.persister(obj));

		// this.actionTracker$.firstOfType(TicketActionTypes.ticketPersistData).pipe(takeUntil(this.shutdownSubject)).subscribe((thing)=>{
		// 	this.logger.log('ticketList actionTracker', thing);
		// })
	}

	//may be some value to building this into the FancyFirebaseList, but it doesn't really do "adds" by default
	private soakPersistence = new SoakPersistence<any>(20, (obj) => {
		return obj.key;
	});
	public editTicket$ = new BehaviorSubject<Map<string, Ticket>>(new Map());
	public editTicketsWithUpdate$: Observable<any>;
	public selectedTicket$ = new BehaviorSubject(new TicketSelection());

	private delayedSelection = new Map<string, string>();

	/** rawEvent$ filtered down to a theoretical subset of "necessary" events */
	public soakedRaw$: Observable<FancyTicketAction>;

	//show longest crap
	//No idea why this is actually an observable, maybe... make it not that at some point
	private pullTicket$ = new BehaviorSubject(new Map<string, Ticket>());
	private lastVarianceList?: Map<string, Variance>;

	private lastSettings: SessionSettings = {};

	//it doesn't really matter if the max is deleted and should technically be reduced, so we'll cheat
	private maxUserOrder = 0;

	protected addActions = ticketAddActions;
	protected removeActions = ticketRemoveActions;
	//special case add actions where it was this client doing the adding, this allows some extra decoration of data
	protected thisClientAddedActions = thisClientTicketAddedActions;

	private plan$ = new BehaviorSubject<Plan>(null);
	private planData: Plan;

	private ticketScale: TicketScale = TicketScale.Daily; // daily mostly for legacy

	protected currentPlanArchive: Map<string, boolean> = new Map();

	private ticketCallbackFns = getTicketExtraCallbacksForPlan(this.plan$, this.workDays);

	private tick500 = renderQueue.ob$.pipe(
		drop(31),
		runInZone(this.angularLand.ngZone),
		takeUntil(this.shutdownSubject)
	);

	private persister(obj: { key: string; data: object | null }) {
		//get ticketRef is safer, for the inherited items
		const ticketRef = this.getTicketRef(obj.key);
		const outputPromise = obj.data === null ? ticketRef.remove() : ticketRef.update(obj.data);

		outputPromise
			// this.listRef.update(obj.key, obj.data)
			.then(
				() => this.actionTracker.actionTracker$.dispatchSuccess(obj),
				(e) => {
					this.actionTracker.actionTracker$.dispatchFailure(obj, e);
				}
			);
	}
	private updateMaxUserOrder(newOrder: number) {
		if (newOrder > this.maxUserOrder) {
			this.maxUserOrder = newOrder;
		}
	}

	public thisClientAddedAction(action: IHasType | string) {
		return isActionIncluded(action, this.thisClientAddedActions);
	}

	private setupBufferedEvents() {
		// TicketActionTypes.ticketChangedToDrag;
		const requiredEvents: any = {
			...this.addActions,
			...this.removeActions,
			[TicketActionTypes.ticketChangedToDrag]: TicketActionTypes.ticketChangedToDrag,
			[TicketActionTypes.ticketSelected]: TicketActionTypes.ticketSelected,
		};
		this.soakedRaw$ = this.getSoakedEvents$(requiredEvents);
		// this.soakedRaw$ = this.rawEvent$;
	}

	/** to be called after "new-ing" it */
	private addExtraTicketData(t: Ticket) {
		decorateTicketExtras(t, {
			...this.ticketCallbackFns,
			varianceList: this.lastVarianceList,
			plan: this.planData,
		});

		if (this.delayedSelection.size && this.delayedSelection.has(t.$id)) {
			t.selected = true;
			this.delayedSelection.delete(t.$id);
		}

		t.externalProperties.shouldHideCompletedTickets = this.lastSettings.hideCompletedTickets;
		t.externalProperties.granularScale = this.ticketScale;

		const planId = this.plan$.getValue()?.$id;
		if (!!planId && this.currentPlanArchive.has(planId)) {
			t.view.isPlanArchived = this.currentPlanArchive.get(planId);
		}
	}

	public localAdd(id, data) {
		const t = new Ticket(null, id);
		this.addExtraTicketData(t);
		t.firebaseUpdate(data);
		return t;
	}

	public $$added(action: AngularFireAction<DatabaseSnapshot<any>>) {
		// this.logger.log('ticket added', action);
		const t = new Ticket(null, action.key);
		//do this thing to preserve "this"
		// t.calculateActuals = (actualFinish, durationDays, roleId, originalWidth)=>{
		// 	return this.calculateTicketActuals(actualFinish, durationDays, roleId, originalWidth);
		// }
		this.updateMaxUserOrder(action.payload.child('userOrder').val());

		this.addExtraTicketData(t);

		t.firebaseUpdate(action.payload.val());

		return t;
	}
	public $$updated(ticket: Ticket, action: AngularFireAction<DatabaseSnapshot<any>>) {
		//debug logging
		// if(fbReporting && !ticket){
		// 	fbReporting.reportRealWarning(
		// 		"ticket undefined in firebaseUpdate call",
		// 		Object.assign(utils.makeStack(new Error("ticket undefined in firebaseUpdate call")), {
		// 			actionKey: action ? action.key : 'undefined',
		// 			actionType: action ? action.type : 'undefined',
		// 			isSelectedTicketsDefined: !!this.selectedTicket$,
		// 			ticketCount: (this._internalList && this._internalList.size) ? this._internalList.size : 0
		// 		}))
		// }
		this.updateMaxUserOrder(action.payload.child('userOrder').val());
		ticket.firebaseUpdate(action.payload.val());
	}

	public $$removed(t: Ticket) {
		//remove external references
		t.getXFromDate = null;
		t.checkForLongestPull = null;
		t.activeLineX = null;
		t.planHasActiveSpace = null;
		t.varianceList = null;
	}

	public setupSelection() {
		this.rawEvent$
			.pipe(
				scan(
					(state: { changed: boolean; selection: TicketSelection }, action: FancyTicketAction) => {
						let changed = false;

						if (action.payload.selected && !state.selection.list.has(action.key)) {
							state.selection.add(action.key, action.payload);
							changed = true;
						} else if (!action.payload.selected && state.selection.list.has(action.key)) {
							state.selection.remove(action.key);
							changed = true;
						}
						//data removed
						else if (state.selection.list.has(action.key) && !this._internalList.has(action.key)) {
							state.selection.remove(action.key);
							changed = true;
						}

						state.selection.updateStats(action.payload);

						state.changed = changed;
						return state;
					},
					{ changed: false, selection: new TicketSelection() }
				),
				filter((state) => state.changed),
				pluck('selection')
			)
			.subscribe(this.selectedTicket$);
	}

	public makePullList() {
		this.rawEvent$
			.pipe(
				scan(
					(state: { changed: boolean; pullTickets: Map<string, Ticket> }, action: FancyTicketAction) => {
						let changed = false;
						// debugger;
						if (action.payload.view.pullColumn !== undefined && !state.pullTickets.has(action.key)) {
							state.pullTickets.set(action.key, action.payload);
							changed = true;
						} else if (action.payload.view.pullColumn !== undefined && state.pullTickets.has(action.key)) {
							// state.pullTickets.delete(action.key);
							changed = false;
						}
						//data removed
						else if (state.pullTickets.has(action.key) && !this._internalList.has(action.key)) {
							state.pullTickets.delete(action.key);
							changed = true;
						}
						state.changed = changed;
						// this.logger.log('state.pullTickets', state.pullTickets);
						return state;
					},
					{ changed: false, pullTickets: new Map() }
				),
				filter((state) => state.changed),
				pluck('pullTickets')
			)
			.subscribe(this.pullTicket$);
	}

	public letEditTicketsPlay() {
		const obs = monitorSubList<Ticket>(
			this.rawEvent$,
			(action) =>
				action.payload.renderType === TicketRenderTypes.edit &&
				// BEGIN PLACEHOLDER REPLACE AS PART OF TP-4914
				action.payload.view.type !== 'masterScheduleTask', //TODO: use value from common
			// END PLACEHOLDER REPLACE AS PART OF TP-4914
			(type) => this.isRemoveAction(type)
		);
		obs.subscribe(this.editTicket$);

		//unsure if this makes sense on second thought. Seems like the edit combined is just going to want to know
		// the normal flow seems like it's not going to want to know about the initial data, so maybe remove that part
		this.editTicketsWithUpdate$ = this.editTicket$.pipe(
			filter((editList) => !!editList),
			switchMap((editList) => {
				// this.logger.log('---editList', editList, editList.size);
				return this.rawEvent$.pipe(filter((action) => editList.has(action.key)));
				// let arr = [];
				// edit.list.forEach(tick => {
				// 	arr.push(new FancyTicketAction(tick, FancyActionTypes.childReplayed));
				// });
				//
				// return concat(
				// 	from(arr),
				// 	this.rawEvent$.pipe( filter((action) => edit.list.has(action.key)) )
				// )
			})
		);
	}

	public handleCustomEvents(baseObservable) {
		return baseObservable.pipe(
			mergeMap((action: CombinedTicketActions) => {
				// this.logger.log('action', action.type);
				var failure = false;
				var ticket = this._internalList.get(action.key);
				switch (action.type) {
					case TicketActionTypes.ticketDragged:
						if (ticket.renderType !== TicketRenderTypes.drag) {
							ticket.changeToSecondaryView(TicketRenderTypes.drag);
						}
						ticket.localUpdate(action.payload, TicketRenderTypes.drag);
						break;
					case TicketActionTypes.ticketChangedToDrag:
						ticket.changeToSecondaryView(TicketRenderTypes.drag);
						break;
					case TicketActionTypes.ticketChangedToEdit:
						ticket.changeToSecondaryView(TicketRenderTypes.edit);
						break;
					case TicketActionTypes.ticketChangedToLocationsEdit:
						ticket.changeToSecondaryView(TicketRenderTypes.locationsEdit);
						break;
					case TicketActionTypes.ticketChangedToDefault:
						ticket.changeToDatabaseView();
						break;
					case TicketActionTypes.ticketViewDataResetToDatabase:
						ticket.changeToSecondaryView(ticket.renderType, true);
						break;
					case TicketActionTypes.ticketSelected:
						ticket.selected = true;
						break;
					case TicketActionTypes.cycleTicketSelected:
						ticket.cycleSelected = true;
						break;
					case TicketActionTypes.ticketUnselected:
						// this.logger.log('ticket', ticket);
						ticket.selected = false;
						break;
					case TicketActionTypes.cycleTicketUnselected:
						ticket.cycleSelected = false;
						break;
					case TicketActionTypes.ticketSelectionDelayed:
						//just select it normally
						if (ticket) {
							ticket.selected = true;
							(<any>action.type) = TicketActionTypes.ticketSelected;
							(<any>action.payload) = ticket;
						} else {
							this.delayedSelection.set(action.key, action.key);
							failure = true;
						}
						break;
					case TicketActionTypes.ticketFocused:
						ticket.selected = true;
						break;
					case TicketActionTypes.ticketLiveUpdate:
					case TicketActionTypes.ticketEditUpdate:
						const view = action.config && action.config.viewOverride ? action.config.viewOverride : undefined;

						if (action.config && action.config.overwrite) {
							ticket.overwriteLocalView(action.payload, view);
						} else {
							ticket.localUpdate(action.payload, view);
						}

						break;
					case TicketActionTypes.ticketPersistData:
						var renderType = action.config && action.config.viewOverride;
						var updateData = ticket.prepareDataForSave(renderType);
						if (action.config && action.config.readOnlyWrite) {
							updateData = Ticket.readOnlyFilter(updateData);
						}
						// debugger;

						// this.logger.log(JSON.stringify(updateData, null, 2));
						this.soakPersistence.push({ key: action.key, data: updateData });
						// this.getTicketRef(action.key).update(updateData)
						// // .then(()=> this.actionTracker$.dispatchSuccess(action))
						// .catch((e) => { failure = true; this.actionTracker.actionTracker$.dispatchFailure(action, e); });

						break;
					case TicketActionTypes.ticketPersistDataLocalOnly:
						var updateData = ticket.prepareDataForSave();
						break;
					//this event does not recieve a ticket, but an object instead
					case TicketActionTypes.floatingTicketAdded:
						var floatingTicket = new FloatingTicket(null);
						this.addExtraTicketData(floatingTicket);

						// BEGIN PLACEHOLDER REPLACE AS PART OF TP-7317
						// this is a hack to maintain compatibility with another substantially older hack
						// - there is code in ticket_handling (makeShapely) that forcibly sets the left of floating tickets
						// pre-drag start (TicketDragService dragStart not the floating one) to 100px greater than the active line
						// - it does this seemingly just to force the side calcuation to always "start" on the pull side
						// - the drag code runs extremely different algorithms based on which side of the active line the ticket started on
						// - aborted the investigation at this point with this hack since the real fix is likely... "massive painful rewrites"
						// to the floating space and drag code
						// - in effect this forces tickets dragged from floating space to start with that value "all" of it's left fields
						// which allows the sketchy black box to maintain it's assumptions. And thus actually show up at all when dragging it
						// out of the pull space
						const pay = action.payload as any;
						pay.leftGranular = pay.left;
						// END PLACEHOLDER REPLACE AS PART OF TP-7317
						floatingTicket.firebaseUpdate(action.payload as any);

						// this.logger.log('floatingTicket', floatingTicket);
						this._internalList.set(floatingTicket.$id, floatingTicket);
						ticket = floatingTicket;
						break;
					case TicketActionTypes.ticketMovedToFloating:
						if (ticket.isAFloatingTicket) {
							this._internalList.delete(ticket.$id);
						} else {
							ticket.externalProperties.ticketMovedToFloating = true;
							ticket.recalcHidden();
							// ticket.hidden = true;
							//change the action type (this is the first time we're doing this sort of thing)
							(<any>action.type) = FancyActionTypes.childChanged;
						}
						break;
					case TicketActionTypes.ticketReturnedToPlan:
						ticket.externalProperties.ticketMovedToFloating = false;
						ticket.recalcHidden();
						// ticket.hidden = false;
						break;
					case TicketActionTypes.floatingTicketSaved:
						ticket = action.payload;
						var data = ticket.prepareDataForSave();
						var id = action.config?.ticketId || this.generateNewChildKey();

						data.id = id;

						var newTicket = this.localAdd(id, data);
						newTicket.selected = ticket.selected;
						this._internalList.set(id, newTicket);
						ticket = newTicket;

						this.soakPersistence.push({ key: id, data });
						// this.listRef.set(id, data)
						// .then(() => this.actionTracker.actionTracker$.dispatchSuccess(action),
						// (e) => {
						// 	failure = true;
						// 	this.actionTracker.actionTracker$.dispatchFailure(action, e);

						// 	// probably not necessary, firebase should emit a child_removed anyways
						// 	// this._internalList.delete(id);
						// 	// this.appEvent$.next(new FancyTicketAction(newTicket, FancyActionTypes.childRemoved))
						// });
						break;
					case TicketActionTypes.addTicket:
						var id = (<any>this.listRef).push().key as string;
						// action.payload.id = id;
						ticket = this.createTicket(action.payload, id, action);
						if (action.config.selectImmediately) {
							ticket.selected = true;
						}
						if (action.config.editImmediately) {
							ticket.changeToSecondaryView(TicketRenderTypes.edit);
						}

						ticket.ticketJustAdded = true;

						break;
					case TicketActionTypes.createTicketWithId:
						// ticket = action.payload;

						ticket = this.createTicket(action.payload, action.key, action);
						if (action.config.selectImmediately) {
							ticket.selected = true;
						}
						break;
					//TODO - add config support for offset config and duplicate count and stripping status
					case TicketActionTypes.ticketDuplicatedInput:
						return this.ticketDuplicatedHandler(action);
						break;
					case TicketActionTypes.deleteTicket:
					case TicketActionTypes.internalDeleteTicket:
						ticket = action.payload;
						this.soakPersistence.push({ key: action.key, data: null });
						// this.listRef.remove(action.key); //sneaky sneaky
						break;
					//this needs to be an action so it can get an id
					case TicketActionTypes.addPinToTicket:
						// ticket = action.payload;

						const newPinParentRef = this.listRef.query.ref.child(ticket.$id).child('promisePeriodStack'); //DEVTEXT - move to function
						const pinList = createPromisePin(
							newPinParentRef,
							ticket,
							action.pinData,
							(ref) => ref.push().key
						);

						ticket.localUpdate({ livePromisePeriodStack: pinList });

						break;
					//this action only exists as a parallel for add
					case TicketActionTypes.removePinFromTicket:
						ticket = action.payload;
						if (ticket.view.livePromisePeriodStack) {
							const stack = cloneStack(ticket.view.livePromisePeriodStack);
							stack.pop();
							ticket.localUpdate({ livePromisePeriodStack: stack.length ? stack : null });
						} else {
							//don't even emit for non-pin ones
							return EMPTY;
						}

						// let newPinRef = this.listRef.query.ref.child(ticket.$id).child('promisePeriodStack'); //DEVTEXT - move to function
						// createPromisePin(newPinRef, ticket, action.pinData)

						break;
					case TicketActionTypes.removeFloatingTickets:
						var output = [];
						this._internalList.forEach((t, key) => {
							if (key.includes('float')) {
								this._internalList.delete(key);
								this.selectedTicket$.pipe(first()).subscribe((selection) => {
									t.selected = false;
								});
								output.push(new FancyTicketAction(t, TicketActionTypes.ticketMovedToFloating));
							}
							//technically this wants to emit a set of actions too, but it shouldn't be necessary in this case... in theory
							else if (t.externalProperties && t.externalProperties.ticketMovedToFloating) {
								delete t.externalProperties.ticketMovedToFloating;
								t.recalcHidden();
							}
						});
						return from(output);
					// case TicketActionTypes.updateDependencyTarget:
					// 	if(!ticket){failure = true; break;} //this can happen on ticket deletion of a dependency target
					// 	ticket.dependencyWeight = action.weightyness;
					// 	break;
					case TicketActionTypes.ticketSavedToFloating:
						break;
					default:
						this.logger.log('action ' + action.type + ': is not being handled', action);
						break;
				}
				if (failure) {
					return EMPTY;
				}
				// return of(new FancyTicketAction(ticket, action.type, ticket ? undefined : action.key));
				return of(new FancyTicketAction(ticket, action.type, null, action.config));
			})
		);
	}

	private createTicket(data, id: string, action?) {
		data.id = id;
		data.userOrder = this.maxUserOrder + 100;

		// TP-626 temp workaround
		let bonusData;
		if (data.ticketExceptionDays) {
			bonusData = {
				ticketExceptionDays: data.ticketExceptionDays,
				//prev plan needs to exist because the new plan's joiner will immediately nuke the old plans value
				// this is only an issue while we require adding tickets to the main ticket list before they can be dragged
				ticketExceptionDaysPrevPlan: data.ticketExceptionDays,
			};
			delete data.ticketExceptionDays;
		}

		const newTicket = this.localAdd(id, data);
		this._internalList.set(id, newTicket);
		const ticket = newTicket;
		// TP-626 temp workaround
		if (bonusData) {
			Object.keys(bonusData).forEach((key) => {
				ticket.database[key] = bonusData[key];
			});
		}

		if (!action) {
			return ticket;
		}

		this.soakPersistence.push({ key: id, data });
		// this.listRef.set(id, data)
		// .then(() => this.actionTracker.actionTracker$.dispatchSuccess(action),
		// (e) => {
		// 	this.actionTracker.actionTracker$.dispatchFailure(action, e);
		// });

		return ticket;
	}

	//overriding the parent to get at the observable when it's passed in. The parent literally has the wrong type,
	//doesn't really seem worth fixing it as everything else here is pretty garbage
	public addSupplementalObservable(obs: Observable<any>, actionType?: FancyActionTypes, key?) {
		super.addSupplementalObservable(obs, actionType);

		obs
			.pipe(
				filter((action) => action.type === SupplementalActionTypes.planUpdated),
				map((action) => action.payload),
				takeUntil(this.shutdownSubject)
			)
			.subscribe(this.plan$);
	}

	public handleSupplementalEvents(baseObservable: Observable<any>): Observable<FancyTicketAction> {
		return baseObservable.pipe(
			groupBy((action) => (<any>action).type),
			mergeMap((ob$) => {
				switch (ob$.key) {
					case SupplementalActionTypes.planUpdated:
						return ob$.pipe(
							debounce(() => this.tick500),
							mergeMap((action) => {
								return merge(this.updatePlanData(action.payload), this.updateLongest(action.payload));
							})
						);
					case SupplementalActionTypes.varianceListUpdated:
						//start with something really simple
						return ob$.pipe(
							debounce(() => this.tick500),
							mergeMap((action) => {
								this.lastVarianceList = action.payload;
								const list = [];
								this._internalList.forEach((t) => {
									const update = t.updateVariance(action.payload);
									if (update) {
										list.push(new FancyTicketAction(t, TicketActionTypes.ticketPlanDataUpdated));
									}
								});
								return from(list);
							})
						);
					case SupplementalActionTypes.sessionSettingsChanged:
						return ob$.pipe(
							mergeMap((action) => {
								return this.updateSessionSettings(action.payload);
							})
						);
					case SupplementalActionTypes.planArchivedChanged:
						return ob$.pipe(
							withLatestFrom(this.list$, this.plan$),
							mergeMap(([action, tickets, plan]) => {
								const emits = [];
								if (plan?.$id) {
									const isPlanArchived: boolean = action.payload.get(plan.$id);

									this.currentPlanArchive = new Map([[plan.$id, isPlanArchived]]);

									tickets.forEach((ticket) => {
										ticket.view.isPlanArchived = isPlanArchived;
										emits.push(new FancyTicketAction(ticket, TicketActionTypes.ticketPlanDataUpdated));
									});
								}
								return from(emits);
							})
						);
					case SupplementalActionTypes.ticketScaleChanged:
						return ob$.pipe(
							mergeMap((action) => {
								return this.updateTicketScale(action.payload);
							})
						);
					default:
						return EMPTY;
				}
			})
		);
	}

	private updateSessionSettings(settings: SessionSettings) {
		//use map so all settings can just dump into here and replace if necessary
		const changedTickets = new Map<string, Ticket>();
		if (this.lastSettings.hideCompletedTickets !== settings.hideCompletedTickets) {
			this._internalList.forEach((tick, key) => {
				tick.externalProperties.shouldHideCompletedTickets = settings.hideCompletedTickets;
				if (tick.recalcHidden()) {
					changedTickets.set(key, tick);
				}
			});
		}

		this.lastSettings = Object.assign({}, settings);

		if (changedTickets.size) {
			const outputList: Array<FancyTicketAction> = [];
			changedTickets.forEach((ticket, key) => {
				outputList.push(new FancyTicketAction(ticket, TicketActionTypes.ticketPlanDataUpdated));
			});
			return from(outputList);
		}

		return EMPTY;
	}

	private updateLongest(plan: Plan) {
		if (!plan) {
			return EMPTY;
		}
		let tickets;
		if (this.suppressActionsForLoading) {
			tickets = this._internalList;
		} else {
			tickets = this.pullTicket$.getValue();
		}

		const changedTickets = [];
		// debugger;
		tickets.forEach((ticket) => {
			//ticket has been removed and the pullcolumn observable hasn't been updated yet
			if (!this._internalList.has(ticket.$id)) {
				return;
			}
			const newIsLongest =
				plan &&
				plan.view.pullColumns &&
				plan.view.pullColumns[ticket.view.pullColumn] &&
				plan.view.pullColumns[ticket.view.pullColumn].plannedStart &&
				plan.view.pullColumns[ticket.view.pullColumn].plannedStart === ticket.view.plannedStart;
			if (newIsLongest && !ticket.isLongestPull) {
				ticket.isLongestPull = true;
				changedTickets.push(new FancyTicketAction(ticket, TicketActionTypes.ticketPlanDataUpdated));
			} else if (!newIsLongest && ticket.isLongestPull) {
				ticket.isLongestPull = false;
				changedTickets.push(new FancyTicketAction(ticket, TicketActionTypes.ticketPlanDataUpdated));
			}
		});

		if (changedTickets.length) {
			return from(changedTickets);
		} else {
			return EMPTY;
		}
	}

	//these supplemental updates apply ticket changes outside of the normal handling flow,
	// the emit is a simple notification and is otherwise unhandled internally
	private updatePlanData(plan: Plan) {
		const usefulProps = ['activeLineDate', 'activeLineX'];
		let exitObs;

		const oldPlanData = this.planData;
		this.planData = plan;

		//interesting data changed
		const partiallyEmpty = !plan !== !oldPlanData;
		// if(plan){ this.logger.log('plan update', plan.activeLineX, plan.activeLineDate); }
		if (
			partiallyEmpty ||
			(oldPlanData && usefulProps.some((key) => oldPlanData.view[key] !== plan.view[key]))
		) {
			const changedTickets = [];
			this._internalList.forEach((ticket) => {
				if (!plan) {
					ticket.activeLineX = null;
					ticket.planHasActiveSpace = false;
				} else {
					ticket.activeLineX = plan.view.activeLineX;
					ticket.planHasActiveSpace = plan.view.planHasActiveSpace;
				}
				let emit = false;
				//make absolutely sure this gets recalced on database as well
				if (ticket.renderType !== TicketRenderTypes.database) {
					emit = ticket.recalcSide(TicketRenderTypes.database) || emit;
				}
				emit = ticket.recalcSide() || emit;
				emit = ticket.recalcPins() || emit;
				if (emit) {
					changedTickets.push(new FancyTicketAction(ticket, TicketActionTypes.ticketPlanDataUpdated));
				}
			});
			exitObs = from(changedTickets);
		} else {
			exitObs = EMPTY;
		}
		if (plan && this.suppressActionsForLoading) {
			this.stopSuppressing$.next(true);
		}
		// this.planData = plan;
		return exitObs;
	}

	private updateTicketScale(scale: TicketScale) {
		const oldScale = this.ticketScale;
		this.ticketScale = scale;

		if (oldScale !== scale) {
			const changedTickets: FancyTicketAction[] = [];
			this._internalList.forEach((ticket) => {
				ticket.externalProperties.granularScale = scale;
				ticket.changeScale(scale);
				changedTickets.push(new FancyTicketAction(ticket, TicketActionTypes.ticketPlanDataUpdated));
			});
			return from(changedTickets);
		} else {
			return EMPTY;
		}
	}

	protected getTicketRef(ticketId) {
		return this.listRef.query.ref.child(ticketId);
	}

	//put in interface so you can explicitly say you're doing this
	public applyJoinData(baseKey: supportedJoinKeys, child: Ticket, otherChild: any) {
		let betterChild = null;
		switch (baseKey) {
			case 'roleId':
				betterChild = otherChild;
				break;
			case 'locationId':
				betterChild = otherChild ? otherChild : null;
				break;
			case 'requestedByProjectMemberId':
				betterChild = otherChild ? otherChild : null;
				break;
			case 'requestedByProjectMemberRoleId':
				betterChild = otherChild ? otherChild : null;
				break;
			case 'responsibleProjectMemberId':
				betterChild = otherChild ? otherChild : null;
				break;
			case 'responsibleProjectMemberRoleId':
				betterChild = otherChild ? otherChild : null;
				break;
			case 'assignedProjectMemberId':
				betterChild = otherChild ? otherChild : null;
				break;
			case 'assignedProjectMemberRoleId':
				betterChild = otherChild ? otherChild : null;
				break;
			case 'priorityId':
				betterChild = otherChild ? otherChild : null;
				break;
			case '$id': //coerce the id for better future compatibility and readability
				betterChild = otherChild ? otherChild : null;
				baseKey = 'ticketExceptionDays';
				break;
			default:
				break;
		}

		if (Ticket.joinKeyMapping[baseKey] !== undefined) {
			const conf = Ticket.joinKeyMapping[baseKey];
			child.view[conf.key] = betterChild;
			if (conf.updateFunctions && conf.updateFunctions.length) {
				conf.updateFunctions.forEach((f) => {
					child[f]();
				});
			}
		}

		child.populatePrev(baseKey);
	}
	public getDataForKey(baseKey: string, child: Ticket) {
		if (baseKey === '$id') {
			return child.$id;
		} //shouldn't ever need a prev since a ticket's id can't change
		return child.view[baseKey];
	}
	public getPrevDataForKey(baseKey: string, child: Ticket) {
		if (baseKey === '$id') {
			return child.prevData['ticketExceptionDays'];
		}
		return child.prevData[baseKey];
	}

	//return observable with many tickets
	private ticketDuplicatedHandler(action: TicketDuplicatedActionInput) {
		if (!action.config) {
			action.config = {};
		}

		const duplicateCount = action.config.countOverride || 1;
		const saveList = [];
		const planId = this.listRef.query.ref.key;

		//additional count loop
		for (let i = 0; i < duplicateCount; i++) {
			const oldToNewId = new Map<string, string>();
			const newTickets = new Map<string, Ticket>();

			//do a first iteration to get the whole set of id's, this should allow mapping as we go
			action.payload.forEach((ticket) => {
				const id = (<any>this.listRef).push().key; //no, this does exist angularfire, you're wrong
				oldToNewId.set(ticket.$id, id);
			});

			const getNewDeps = (data: IPredecessorData) => {
				if (!data || !data[planId]) {
					return null;
				}
				const newObj = { [planId]: {} };
				const base = newObj[planId];
				let assignedCount = 0;
				Object.keys(data[planId]).forEach((oldPredTicketId) => {
					const newPredTicketId = oldToNewId.get(oldPredTicketId);
					if (!newPredTicketId) {
						// Beware of broken dependencies not fully duplicated
						return;
					}
					// Here, copy the default data, since it includes the defaults for "isCyclic"
					// and "isOutOfOrder" (both of which are ultimately updated by the backend),
					// but then overwrite the "linkageType" and "lagDays", copying those from the
					// source dependency:
					base[newPredTicketId] = {
						...defaultDependencyData,
						linkageType: data[planId][oldPredTicketId].linkageType ?? null,
						lagDays: data[planId][oldPredTicketId].lagDays ?? null,
					};
					assignedCount++;
				});
				return assignedCount ? newObj : null;
			};

			action.payload.forEach((ticket) => {
				//different for pull and active tickets
				let duplicateOffset = Object.assign(
					{},
					ticket.view.side === Side.ACTIVE
						? defaultActiveTicketDuplicateOffset
						: defaultPullTicketDuplicateOffset
				);

				if (action.config.duplicateOffset) {
					duplicateOffset = action.config.duplicateOffset;
				}

				const data = ticket.prepareDataForSave();
				if (!action.config.dontStripStatus) {
					ticketCopy.stripTicketStatus(data);
				}
				data.preservedTicketId = null;
				data.left = (data.left || 0) + duplicateOffset.x * (i + 1);
				data.top = (data.top || 0) + duplicateOffset.y * (i + 1);
				data.predecessors = getNewDeps(data.predecessors);

				const id = oldToNewId.get(ticket.$id);
				const newTicket = this.createTicket(data, id, action);

				//update mappings
				newTickets.set(id, newTicket);

				//note that creating the action then manipulating the newTicket only works because references.
				//need to do this so that the initial set of join data is applied before switching to the edit view
				const newAction = new FancyTicketAction(newTicket, TicketActionTypes.ticketDuplicated);
				this.applyFancyJoinDataToBaseAction(newAction);

				if (action.config.editImmediately) {
					newTicket.changeToSecondaryView(TicketRenderTypes.edit);
				}
				if (action.config.selectImmediately) {
					newTicket.selected = true;
				}

				newAction.config = { oldTicketId: ticket.$id }; // grab the old ticket id as part of payload for copying custom field data
				saveList.push(newAction);
			});
		}

		return from(saveList);
	}

	public destroy() {
		this.workDays = null;
		this.lastVarianceList = null;

		this.selectedTicket$.complete();
		this.selectedTicket$ = null;
		this.pullTicket$.complete();
		this.pullTicket$ = null;
		this.editTicket$.complete();
		this.editTicket$ = null;

		this.plan$.complete();

		this.soakPersistence.destroy();

		this._internalList.forEach((t) => this.$$removed(t));

		super.destroy();
		this.soakedRaw$ = null;
	}
}
